/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles, Theme} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {FloatingIconButtonGroup} from '@ion/dls/src/components/buttons/FloatingIconButton';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {useIsPhone} from '@ion/dls/src/hooks/useMedia';
import {MapZoomInIcon} from '@ion/dls/src/icons/MapZoomInIcon';
import {MapZoomOutIcon} from '@ion/dls/src/icons/MapZoomOutIcon';
import {IconSize} from '@ion/dls/src/types/types';

// Defines the props for the ZoomInOutTool component.
export type ZoomInOutProps = {
  onZoomIn: React.MouseEventHandler;
  onZoomOut: React.MouseEventHandler;
  isVertical?: boolean;
};

/**
 * The ZoomInOutTool component provides the UI which displays the associated Icon
 * button within a SimpleMap that, when clicked, initiates the corresponding
 * feature.
 *
 * This ZoomInOutTool represents the "Zoom In and Zoom Out Tool" DLS features:
 * https://www.figma.com/file/srjlHrDuM90sCoaOwpjaDU/01_DLS_Components?node-id=1526%3A7422
 *
 * ```typescript
 *
 *   <ZoomInOutTool onZoomOut={function() {
 *     const {zoom} = view;
 *     view.goTo({zoom: zoom - 1});
 *   }} onZoomIn={function() {
 *     const {zoom} = view;
 *     view.goTo({zoom: zoom + 1});
 *   }} />
 *
 * ```
 */
const useStyles = makeStyles((theme: Theme) => ({
  btnGroup : {
    marginLeft : '2px'
  }
}));

export const ZoomInOutTool = ({
  onZoomIn,
  onZoomOut,
  isVertical = true
}: ZoomInOutProps) => {
  const isPhone = useIsPhone();
  const classes = useStyles();
  return (
    <FloatingIconButtonGroup isVertical={isVertical}>
      <>

        <Tooltip title={translate('maps:controls.zoomIn')} renderOnlyChildren={isPhone}>
          <div>
            <FloatingIconButtonGroup.FloatingIconButton onClick={onZoomIn}>
              <MapZoomInIcon size={IconSize.medium} />
            </FloatingIconButtonGroup.FloatingIconButton>
          </div>
        </Tooltip>

        <Tooltip title={translate('maps:controls.zoomOut')} renderOnlyChildren={isPhone}>
          <div className={!isVertical && classes.btnGroup }>
            <FloatingIconButtonGroup.FloatingIconButton onClick={onZoomOut}>
              <MapZoomOutIcon size={IconSize.medium} />
            </FloatingIconButtonGroup.FloatingIconButton>
          </div>
        </Tooltip>
      </>
    </FloatingIconButtonGroup>
  );
};

export default ZoomInOutTool;
